<script setup lang="ts">
import type { Feature } from "@/auth/usePermissions";
import MainLogo from "@/components/MainLogo.vue";
import { useLogout } from "@/login/useLogout";
import {
  ArrowLeft,
  Community,
  Heart,
  HelpCircle,
  Home,
  Map,
  Menu,
  Neighbourhood,
  Settings,
  StatUp,
  User,
  UserCircle,
  Xmark,
} from "@iconoir/vue";
import { ref } from "vue";

defineProps<{
  canAccess: (feature: Feature) => boolean;
}>();

const appRevision = import.meta.env.VITE_APP_REVISION;

const navOpen = ref(false);
const toggleNav = () => {
  navOpen.value = !navOpen.value;
};

const { logout } = useLogout();
</script>
<template>
  <nav aria-labelledby="main-menu-label">
    <h2 id="main-menu-label" class="visually-hidden">Hauptnavigation</h2>

    <div class="toggle-bar row box">
      <button
        class="button"
        :aria-expanded="navOpen"
        aria-controls="main-nav"
        @click="toggleNav()"
      >
        <template v-if="navOpen">
          <span class="visually-hidden">Navigation schließen</span>
          <Xmark />
        </template>
        <template v-else>
          <span class="visually-hidden">Navigation öffnen</span>
          <Menu />
        </template>
      </button>
      <RouterLink to="/" class="row-self-space-auto">
        <MainLogo class="navbar-logo" />
      </RouterLink>
    </div>

    <div
      id="main-nav"
      class="nav-wrapper box column column-xl"
      :class="{ 'nav-open': navOpen }"
    >
      <div class="top-wrapper">
        <span
          v-if="canAccess('earlyAccessBadge')"
          class="badge badge-early-access"
        >
          Early Access
          <span v-if="appRevision"> | Version {{ appRevision }} </span>
        </span>
        <RouterLink to="/">
          <MainLogo class="sidebar-logo" />
        </RouterLink>
      </div>

      <section class="organisation">
        <h3>Organisation</h3>
        <p>{{ $config.organizationName }}</p>
      </section>

      <section>
        <ul role="list">
          <li>
            <RouterLink class="nav-item" to="/">
              <Home />
              Dashboard
            </RouterLink>
          </li>
          <li v-if="canAccess('canvasses')">
            <RouterLink class="nav-item" to="/canvasses">
              <Heart />
              Haustüraktionen
            </RouterLink>
          </li>
          <li v-if="canAccess('contacts')">
            <RouterLink class="nav-item" to="/contacts">
              <Community />
              Kontakte
            </RouterLink>
          </li>
          <li v-if="canAccess('areas')">
            <RouterLink class="nav-item" to="/areas">
              <Map />
              Gebiete
            </RouterLink>
          </li>
          <li v-if="canAccess('houses')">
            <RouterLink class="nav-item" to="/houses">
              <Neighbourhood />
              Häuser
            </RouterLink>
          </li>
          <li v-if="canAccess('users')">
            <RouterLink class="nav-item" to="/users">
              <User />
              Nutzer:innen
            </RouterLink>
          </li>
          <li v-if="canAccess('stats')">
            <RouterLink class="nav-item" to="/stats"
              ><StatUp />
              Statistiken
            </RouterLink>
          </li>
        </ul>
        <hr class="divider" />
        <ul role="list">
          <li>
            <RouterLink class="nav-item" to="/account">
              <UserCircle />
              Mein Konto
            </RouterLink>
          </li>
          <li v-if="canAccess('settings')">
            <RouterLink class="nav-item" to="/settings">
              <Settings />Einstellungen
            </RouterLink>
          </li>
          <li>
            <RouterLink class="nav-item" to="/support">
              <HelpCircle />Support
            </RouterLink>
          </li>
          <li>
            <button class="nav-item" @click="logout()">
              <ArrowLeft />
              Ausloggen
            </button>
          </li>
        </ul>
      </section>
    </div>
  </nav>
</template>

<style scoped>
nav {
  position: sticky;
  inset-block-start: 0;
  z-index: 1;
  inline-size: 100%;
  background-color: var(--bg-color-nav);

  --nav-toggle-bar-height: 4rem;
  --nav-item-border-width: 4px;
}

.navbar-logo {
  block-size: 2em;
}

.nav-wrapper {
  display: none;
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  min-inline-size: var(--inline-size-sm);
  block-size: 100vh;
  /* XXX safari 18.7 does not support the logical property overflow-block */
  overflow-y: auto;
  background-color: var(--bg-color-nav);
  padding-block-start: var(--nav-toggle-bar-height);
  max-width: 288px;

  &.nav-open {
    /* revert display: none to whatever was set by the layout classes */
    display: revert-layer;
  }

  .top-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    inline-size: 100%;
    gap: var(--spacing-lg);
  }

  .sidebar-logo {
    inline-size: 100%;
    block-size: auto;
    max-inline-size: 10rem;
  }

  .badge-early-access {
    inline-size: fit-content;
    white-space: nowrap;
    font-size: var(--font-size-sm);
    padding-block: calc(var(--spacing-xs) / 2);
    border: 1px solid var(--text-color-muted);
  }
}

[aria-controls="main-nav"] {
  /* z-index needs position to be anything but static (default) */
  position: relative;
  z-index: 1;
  background-color: var(--bg-color-nav);
  border-color: transparent;
  color: var(--text-color-body);
  box-shadow: none;
}

.toggle-bar {
  height: var(--nav-toggle-bar-height);

  p {
    font-size: var(--font-size-h2);
  }
}

ul {
  display: flex;
  flex-direction: column;
  inline-size: 100%;
}

.divider {
  border: none;
  margin-block: var(--spacing-md);
  margin-inline-start: calc(var(--nav-item-border-width) + var(--spacing-sm));
  margin-inline-end: var(--spacing-sm);
  background-color: var(--text-color-muted);
  height: 1px;
}

li > .nav-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  inline-size: 100%;
  cursor: pointer;
  text-decoration: none;
  color: var(--text-color-body);
  padding-block: var(--spacing-sm);
  padding-inline: var(--spacing-sm);
  border: none;
  border-left: var(--nav-item-border-width) solid transparent;
  border-radius: 0;
  background-color: transparent;
  box-shadow: none;
  text-align: left;

  &.router-link-active {
    border-left: var(--nav-item-border-width) solid var(--color-primary);
    color: var(--color-primary);
  }

  &:hover,
  &:focus-visible {
    border-left: var(--nav-item-border-width) solid var(--color-primary);
  }
}

.organisation {
  h3 {
    font-size: var(--font-size-default);
    font-weight: var(--font-weight-default);
  }

  p {
    font-size: var(--font-size-h2);
    font-weight: var(--font-weight-h2);
  }
}

@media (min-width: 40rem) {
  nav {
    inline-size: fit-content;
    position: static;
  }

  .toggle-bar {
    display: none;
  }

  .nav-wrapper {
    position: static;
    /* revert display: none to whatever was set by the layout classes */
    display: revert-layer;
  }
}
</style>
